/**
 * RuleActionItem — one checkable action, with the consequence spelled out.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The sub-line is the whole point: "Eliminate" alone teaches nobody, "off the
 * tracker for good — the bank keeps it" teaches everybody. Disabled items keep
 * their reason, "soon" items stay visible so the roadmap is legible.
 */

import { FC } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { RuleActionLook } from '../rule-action-meta';

export interface RuleActionItemProps {
  action: RuleActionLook;
  checked: boolean;
  disabled: boolean;
  hint?: string;
  onToggle: (checked: boolean) => void;
}

export const RuleActionItem: FC<RuleActionItemProps> = ({
  action,
  checked,
  disabled,
  hint,
  onToggle,
}) => (
  <DropdownMenu.CheckboxItem
    checked={checked}
    disabled={disabled}
    onCheckedChange={(c) => onToggle(c === true)}
    onSelect={(e) => e.preventDefault()}
    className={cn(
      'flex items-start gap-2 rounded px-2 py-2 text-xs outline-none',
      disabled
        ? 'cursor-not-allowed text-muted-foreground/60'
        : 'cursor-pointer hover:bg-muted focus:bg-muted',
    )}
  >
    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-border">
      <DropdownMenu.ItemIndicator>
        <Check className="h-3 w-3 text-primary" aria-hidden />
      </DropdownMenu.ItemIndicator>
    </span>
    <action.Icon
      className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', !disabled && action.tone)}
      aria-hidden
    />
    <span className="flex-1">
      {action.label}
      {hint ? (
        <span className="mt-0.5 block text-[10px] leading-tight text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </span>
    {action.soon ? (
      <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
        soon
      </span>
    ) : null}
  </DropdownMenu.CheckboxItem>
);

export default RuleActionItem;
